import { useMemo } from 'react';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert } from '@launchpad-ui/components';

import { MS_PER_DAY } from '../../../../constants';
import type { DimensionType, UsageTabKey } from '../../../../types';
import { AggregationType, Granularity } from '../../../../types';
import { usageTabConfig } from '../../../constants/usageTabConfig';
import { useGroupedUsageTabData } from '../../../hooks/useGroupedUsageTabData';
import { useUsageData } from '../../../hooks/useUsageData';
import { formatDateUTC, getPreviousTimestampsUTC } from '../../../utils/dates';
import { getGroupByMetadataKeys } from '../../../utils/getGroupByDimensions';
import { getUsageSummaryTooltip } from '../../../utils/getUsageDisplayName';

import { getGroupedTitle } from './UsageDetailGrouped';
import { DimensionSummaryCard, UsageSummaryCard } from './UsageSummaryCard';

import styles from './UsageSummaryCards.module.css';

export type UsageSummaryProps = {
  usageTab: UsageTabKey;
  toTimestampUTC: number;
  today: Date;
  dimension?: DimensionType;
  precision?: number;
  defaultAggregationType?: AggregationType;
};

function UsageSummaryCardsContent({
  usageTab,
  toTimestampUTC,
  today,
  dimension,
  precision,
  defaultAggregationType = AggregationType.MONTH_TO_DATE,
}: UsageSummaryProps) {
  const date = new Date(toTimestampUTC - MS_PER_DAY);
  const utcDate = Date.UTC;
  const startOfMonthUTC = utcDate(date.getUTCFullYear(), date.getUTCMonth(), 1);
  const previousTimestamps = useMemo(() => getPreviousTimestampsUTC(startOfMonthUTC, today), [startOfMonthUTC, today]);

  const aggregationType = usageTabConfig[usageTab].totalAggregationTypeOverride ?? defaultAggregationType;
  const summaryTooltip = getUsageSummaryTooltip(usageTab);

  // Compare the selected month-to-date's usage to the same date range in the previous month, regardless of time range.
  const {
    isLoading,
    isError,
    total: currentTotal,
  } = useUsageData({
    usageTab,
    aggregationType,
    from: startOfMonthUTC,
    to: toTimestampUTC,
    granularity: Granularity.DAILY,
  });

  const {
    data: groupedData,
    sortedTotals: groupedSortedTotals,
    metadataKey,
    isLoading: isGroupedLoading,
    isError: isGroupedError,
  } = useGroupedUsageTabData({
    usageTab,
    fromTimestampUTC: startOfMonthUTC,
    toTimestampUTC,
    aggregationType,
    dimension,
    granularity: Granularity.DAILY,
  });

  const {
    data: previousGroupedData,
    isLoading: isPreviousGroupedLoading,
    isError: isPreviousGroupedError,
  } = useGroupedUsageTabData({
    usageTab,
    fromTimestampUTC: previousTimestamps.from,
    toTimestampUTC: previousTimestamps.to,
    aggregationType,
    dimension,
    granularity: Granularity.DAILY,
  });

  const inclusiveEndDateUTC = new Date(toTimestampUTC - MS_PER_DAY);
  const endDateIsToday = toTimestampUTC > today.getTime();

  if (!dimension) {
    return (
      <div className={styles.cardsContainer}>
        <div className={styles.summary}>
          <UsageSummaryCard
            usageTab={usageTab}
            title={
              endDateIsToday
                ? msg('billing.usageSummary.asOfToday')
                : msg('billing.usageSummary.asOfDate', { date: formatDateUTC(inclusiveEndDateUTC) })
            }
            titleTooltip={summaryTooltip}
            total={currentTotal}
            isTotalLoading={isLoading}
            isTotalError={isError}
            previousFromTimestampUTC={previousTimestamps.from}
            previousToTimestampUTC={previousTimestamps.to}
            aggregationType={aggregationType}
            precision={precision}
          />
        </div>
      </div>
    );
  }

  const groupByMetadataKeys = getGroupByMetadataKeys(usageTab, dimension);

  return (
    <div className={styles.cardsContainer}>
      <div className={styles.title}>
        {endDateIsToday ? (
          <Message id="billing.usageSummary.breakdownAsOfToday" />
        ) : (
          <Message id="billing.usageSummary.breakdownAsOfDate" values={{ date: formatDateUTC(inclusiveEndDateUTC) }} />
        )}
      </div>
      <div className={styles.summaryWithDimensions}>
        <UsageSummaryCard
          usageTab={usageTab}
          title={msg('billing.usageSummary.total')}
          titleTooltip={summaryTooltip}
          total={currentTotal}
          isTotalLoading={isLoading || isGroupedLoading}
          isTotalError={isError}
          previousFromTimestampUTC={previousTimestamps.from}
          previousToTimestampUTC={previousTimestamps.to}
          aggregationType={aggregationType}
          precision={precision}
        />
        {isGroupedError ? (
          <Alert status="error" className={styles.errorAlert}>
            <Message id="billing.usageSummary.errorLoadingDimension" />
          </Alert>
        ) : (
          groupedSortedTotals?.slice(0, 5).map(([total, metadataIdx]) => (
            <DimensionSummaryCard
              key={metadataIdx}
              title={getGroupedTitle({
                dimension,
                metadataKey,
                metadata: groupedData?.metadata?.[Number(metadataIdx)],
              })}
              total={total}
              isTotalLoading={isGroupedLoading}
              previousGroupedData={previousGroupedData}
              isPreviousLoading={isPreviousGroupedLoading}
              isPreviousError={isPreviousGroupedError}
              groupByMetadataKeys={groupByMetadataKeys}
              aggregationType={aggregationType}
              metadata={groupedData?.metadata?.[Number(metadataIdx)]}
              precision={precision}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function UsageSummaryCards(props: UsageSummaryProps) {
  return (
    <QuietErrorBoundary>
      <UsageSummaryCardsContent {...props} />
    </QuietErrorBoundary>
  );
}
